/** @jsx jsx */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box, Flex } from '@chakra-ui/core';
import { motion } from 'framer-motion';
import _union from 'lodash/union';
import _uniqBy from 'lodash/uniqBy';
import moment from 'moment-timezone';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { jsx } from 'theme-ui';
import { AppLoadingSpinner } from '../../app/components';
import { Header } from '../../app/topBar/Header';
import {
  ActiveAppointmentAvailabilitySlot,
  AppointmentAvailability,
} from '../../firebase/firestore/documents/appointmentAvaiability';
import { Recruiter } from '../../firebase/firestore/documents/recruiter';
import { useStoreState } from '../../models/hooks';
import useAccessibleAccounts from '../messaging/seeker/candidate/notes/useAccessibleAccounts';
import AvailabilityCalendar from './availabilityCanendar/AvailabilityCalendar';
import CalendarSidebar from './CalendarSidebar';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from './CalendarSidebarTab';
import { calendarCSS, calendarGridCSS } from './CalendarView.Style';
import { reactBigCalendarCSS } from './ReactBigCalendar.Style';
import { BookingSchedule } from './scheduleSidebar/BookingSchedule';
import { BusinessPositionT } from './types';

require('moment/locale/en-gb.js');
require('moment/locale/fr.js');

const MotionBox = motion.custom(Box);

export type CalendarPanelProps = {
  todayDate: Date;
  recruiters: Recruiter[];
  administrationCalendar: boolean;
  handleCheckedRecruiters?: (recruiterIds: any) => void;
  checkedRecruiters?: string[];
  handleCheckedPositions?: (positionIds: BusinessPositionT[]) => void;
  checkedPositions?: string[];
  // positions?: Position[];
  activeTab?: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum;
  setActiveTab?: (activeTab: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum) => void;
  activeCurrentDateRange: {
    fromDate: Date;
    toDate: Date;
  };
  setActiveDateRange: (fromDate: Date, toDate: Date) => void;
  isLoading?: boolean;
  isAvailabilityLoading?: boolean;
};

export const CalendarPanel = ({
  todayDate,
  recruiters,
  administrationCalendar,
  handleCheckedRecruiters,
  checkedRecruiters,
  // positions,
  handleCheckedPositions,
  checkedPositions,
  activeTab,
  setActiveTab,
  activeCurrentDateRange,
  setActiveDateRange,
  isLoading = false,
  isAvailabilityLoading = false,
}: CalendarPanelProps): JSX.Element => {
  const { i18n, t } = useTranslation('calendar');
  const location = useLocation();
  const history: any = useHistory();
  const accessibleAccount = useAccessibleAccounts();

  const mergedRecruiters = _uniqBy(_union(recruiters, accessibleAccount), 'id');

  const { calendarloading, groupEventsloading } = useStoreState((s) => s.calendar);

  const [activeAppointmentSlot, setActiveAppointmentSlot] = React.useState<ActiveAppointmentAvailabilitySlot | undefined>();

  // istanbul ignore next
  if (i18n.language === 'en') {
    moment.locale('en', {
      week: {
        dow: 1,
        doy: 1,
      },
    });
  }

  // istanbul ignore next
  if (i18n.language === 'fr') {
    moment.locale('fr');
  }

  const [selectedSchedule, setSelectedSchedule] = React.useState<AppointmentAvailability>();
  const [activeDate, setActiveDate] = React.useState<Date>(todayDate);
  const [activeAvailability, setActiveAvailability] = React.useState<AppointmentAvailability | undefined>();
  const [isAppointmentDrawerOpen, setIsAppointmentDrawerOpen] = React.useState<boolean>(!!location.state);

  // to reset location.state value
  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, [history]);

  const closeAppointmentDrawer = () => {
    setIsAppointmentDrawerOpen(false);
  };
  const redirectToPreviousPageHandler = () => {
    if (location.state) {
      // eslint-disable-next-line
      history.goBack();
    }
  };

  return (
    <MotionBox css={[calendarGridCSS, reactBigCalendarCSS]} initial="initial" data-testid="CalendarPanel">
      <Header data-testid="Header" showMenu showSearchSort={false} />
      <Box css={calendarCSS}>
        <Flex w="100%" h="100%">
          <CalendarSidebar
            recruiters={mergedRecruiters}
            administrationCalendar={administrationCalendar}
            handleCheckedRecruiters={handleCheckedRecruiters}
            checkedRecruiters={checkedRecruiters}
            handleCheckedPositions={handleCheckedPositions}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            activeDate={activeDate}
            activeAvailability={activeAvailability}
            setIsAppointmentDrawerOpen={setIsAppointmentDrawerOpen}
            setSelectedSchedule={setSelectedSchedule}
            activeCurrentDateRange={activeCurrentDateRange}
          />
          <Box className="calendar-content" w="66%" flex="1" position="relative">
            {(isLoading || isAvailabilityLoading) && (
              // istanbul ignore next
              <Box
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                zIndex={1000}
                sx={{ backdropFilter: 'blur(10px)' }}
              >
                <AppLoadingSpinner loadingText={t('calendar:loadingCalendar')} />
              </Box>
            )}
            {calendarloading || groupEventsloading ? (
              <AppLoadingSpinner />
            ) : (
              <AvailabilityCalendar
                checkedPositions={checkedPositions}
                mergedRecruiters={mergedRecruiters}
                administrationCalendar={administrationCalendar}
                activeTab={activeTab}
                setActiveDateRange={setActiveDateRange}
                setSelectedSchedule={setSelectedSchedule}
                setActiveAppointmentSlot={setActiveAppointmentSlot}
                setIsAppointmentDrawerOpen={setIsAppointmentDrawerOpen}
                activeDate={activeDate}
                setActiveDate={setActiveDate}
                activeAvailability={activeAvailability}
                setActiveAvailability={setActiveAvailability}
              />
            )}
          </Box>
        </Flex>
      </Box>

      {isAppointmentDrawerOpen && (
        <BookingSchedule
          isOpen={isAppointmentDrawerOpen}
          onClose={() => {
            closeAppointmentDrawer();
            setActiveAppointmentSlot(undefined);
          }}
          redirectToPreviousPageHandler={redirectToPreviousPageHandler}
          activeAppointmentSlot={activeAppointmentSlot}
          scheduleDetail={selectedSchedule}
        />
      )}
    </MotionBox>
  );
};
